import { z } from 'zod';

/**
 * Platform-wide operator settings — never tenant data. Gated on
 * `platform.settings.*`, which only `PLATFORM_SUPER_ADMIN` ever holds.
 */

export const updateSmtpSettingsRequestSchema = z.object({
  host: z.string().trim().min(1).max(255),
  port: z.coerce.number().int().min(1).max(65535),
  useImplicitTls: z.boolean(),
  username: z.string().trim().min(1).max(320),
  /** Omitted to keep the currently stored password unchanged. */
  password: z.string().min(1).max(500).optional(),
  senderAddress: z.email().max(320),
  replyTo: z.email().max(320).nullish(),
});
export type UpdateSmtpSettingsRequest = z.infer<typeof updateSmtpSettingsRequestSchema>;

export const sendTestEmailRequestSchema = z.object({
  to: z.email().max(320),
});
export type SendTestEmailRequest = z.infer<typeof sendTestEmailRequestSchema>;

/** The source `EMAIL_PROVIDER` actually resolved from at boot — a stored
 *  row overrides SMTP_* env vars, so an operator's eventual answer to
 *  "which one wins" is exactly what this reports. */
export const SMTP_SETTINGS_SOURCES = ['database', 'environment', 'unset'] as const;
export type SmtpSettingsSource = (typeof SMTP_SETTINGS_SOURCES)[number];

export interface SmtpSettingsResponse {
  readonly host: string;
  readonly port: number;
  readonly useImplicitTls: boolean;
  readonly username: string;
  /** Never the password itself — only whether one is stored. */
  readonly hasPassword: boolean;
  readonly senderAddress: string;
  readonly replyTo: string | null;
  readonly source: SmtpSettingsSource;
  readonly updatedAt: string | null;
  readonly updatedByUserId: string | null;
}

export const STORAGE_DRIVERS = ['local', 's3'] as const;
export type StorageDriver = (typeof STORAGE_DRIVERS)[number];

export const updateStorageSettingsRequestSchema = z
  .object({
    driver: z.enum(STORAGE_DRIVERS),
    s3Bucket: z.string().trim().max(255).nullish(),
    s3Region: z.string().trim().max(100).nullish(),
    s3Endpoint: z.string().trim().max(500).nullish(),
    s3AccessKeyId: z.string().trim().max(255).nullish(),
    /** Omitted to keep the currently stored secret key unchanged. */
    s3SecretAccessKey: z.string().min(1).max(500).optional(),
    s3ForcePathStyle: z.boolean().default(true),
  })
  .refine((value) => value.driver !== 's3' || (value.s3Bucket && value.s3AccessKeyId), {
    message: 'A bucket and access key ID are required when the driver is s3',
    path: ['s3Bucket'],
  });
export type UpdateStorageSettingsRequest = z.infer<typeof updateStorageSettingsRequestSchema>;

/** The source `OBJECT_STORAGE` actually resolved from at boot — a stored
 *  row overrides STORAGE_DRIVER/S3_* env vars, so an operator's eventual
 *  answer to "which one wins" is exactly what this reports. */
export const STORAGE_SETTINGS_SOURCES = ['database', 'environment', 'unset'] as const;
export type StorageSettingsSource = (typeof STORAGE_SETTINGS_SOURCES)[number];

export interface StorageSettingsResponse {
  readonly driver: StorageDriver;
  readonly s3Bucket: string | null;
  readonly s3Region: string | null;
  readonly s3Endpoint: string | null;
  readonly s3AccessKeyId: string | null;
  /** Never the secret key itself — only whether one is stored. */
  readonly hasS3SecretAccessKey: boolean;
  readonly s3ForcePathStyle: boolean;
  readonly source: StorageSettingsSource;
  readonly updatedAt: string | null;
  readonly updatedByUserId: string | null;
}
